import React, { useMemo, useState } from 'react';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { hexToRgba, useStacklineTheme } from '@stackline/ui';
import { Text } from '../../Generic/Text';
import _sortBy from 'lodash/sortBy';
import { SxProps } from '@mui/system';

export interface SlDropdownMenuOption {
  /**
   * Unique identifier of the menu option
   */
  id: string;
  /**
   * Label to apply to the dropdown option
   */
  label: string;
  /**
   * Boolean to gray out/disable the option
   */
  disabled?: boolean;
  description?: string;

  /**
   * FieldName that is override default id for advanced searh reqeust
   */
  fieldName?: string;
}

export interface SlMenuProps extends Omit<MenuProps, 'onChange'> {
  /**
   * List of options to display
   */
  options: SlDropdownMenuOption[];
  /**
   * ID of the selected item, or null if nothing
   * is selected
   */
  selectedId: string | null;
  /**
   * Callback for when an item is selected
   */
  onChange: (option: SlDropdownMenuOption) => void;
  /**
   * If set, we will show n number of options
   * with a "+ View More" option at the bottom
   */
  amountOfOptionsToShow?: number;
  /**
   * If true, shows only enabled (non-disabled) options by default.
   * Disabled options are revealed when the user clicks "+ View More".
   */
  hideDisabledUntilExpanded?: boolean;
  /**
   * Optional styles to apply to the dropdown menu items text component
   */
  menuItemTextStyles?: SxProps;

  fixSelectedId?: boolean;
}

export const SlMenu = ({
  options,
  onChange,
  selectedId,
  onClose,
  amountOfOptionsToShow,
  hideDisabledUntilExpanded,
  menuItemTextStyles,
  fixSelectedId = true,
  sx,
  ...rest
}: SlMenuProps) => {
  const theme = useStacklineTheme();
  const hoverBgColor = hexToRgba(theme.colors.secondaryPorcelain, 1);
  const selectedBackground = hexToRgba(theme.colors.primaryLight, 0.3);

  const shouldCollapseByDefault = hideDisabledUntilExpanded || !!amountOfOptionsToShow;
  const [showAll, setShowAll] = useState(!shouldCollapseByDefault);

  const sortedOptions = fixSelectedId ? _sortBy(options, (o) => (o.id === selectedId ? 0 : 1)) : options;

  const visibleOptions = useMemo(() => {
    if (showAll) return sortedOptions;
    if (hideDisabledUntilExpanded) {
      return sortedOptions.filter((o) => !o.disabled);
    }
    return sortedOptions.slice(0, amountOfOptionsToShow);
  }, [showAll, hideDisabledUntilExpanded, sortedOptions, amountOfOptionsToShow]);

  const hasHiddenOptions = useMemo(() => {
    if (hideDisabledUntilExpanded) {
      return sortedOptions.some((o) => o.disabled);
    }
    return !!amountOfOptionsToShow && options.length > amountOfOptionsToShow;
  }, [hideDisabledUntilExpanded, sortedOptions, amountOfOptionsToShow, options.length]);

  return (
    <Menu
      sx={{
        ...{
          width: '100%',
          padding: 0,
          '& ul': {
            paddingY: 0,
            paddingRight: 0
          },
          '& .MuiPaper-root': {
            marginTop: '8px',
          }
        },
        ...sx
      }}
      onClose={(...args) => {
        setShowAll(false);
        onClose(...args);
      }}
      transitionDuration={0}
      {...rest}
    >
      {visibleOptions.map((option, index) => {
        const isSelected = selectedId === option.id;
        const isDisabled = !!option.disabled;
        return (
          <MenuItem
            key={`retailer:${option.id}`}
            disabled={isDisabled}
            sx={{
              '&:hover': {
                backgroundColor: isSelected ? selectedBackground : hoverBgColor
              },
              backgroundColor: isSelected ? selectedBackground : undefined,
              borderBottom: index !== visibleOptions.length - 1 ? '1px solid #E8E8ED' : undefined,
              width: '100%'
            }}
            onClick={() => {
              onChange(option);
              if (onClose) {
                onClose({}, 'backdropClick');
              }
            }}
          >
            <Text
              color={isDisabled ? 'secondary' : undefined}
              sx={{
                textOverflow: 'ellipsis',
                width: '100%',
                maxWidth: '100%',
                overflowX: 'hidden',
                ...menuItemTextStyles
              }}
              variant="subtitle2"
            >
              {option.label}
            </Text>
            {option.description && (
              <Text
                variant="subtitle2"
                sx={{
                  fontWeight: 400
                }}
              >
                {option.description}
              </Text>
            )}
          </MenuItem>
        );
      })}
      {!showAll && hasHiddenOptions ? (
        <MenuItem
          sx={{
            '&:hover': {
              backgroundColor: selectedBackground
            },
            borderTop: '1px solid #E8E8ED',
            width: '100%',
            '.MuiMenuItem-root': {
              paddingLeft: '12px',
              paddingRight: '24px'
            }
          }}
          onClick={() => {
            setShowAll(true);
          }}
        >
          <Text sx={{ ...menuItemTextStyles }} variant="subtitle2">
            + View More
          </Text>
        </MenuItem>
      ) : null}
      {showAll && hasHiddenOptions ? (
        <MenuItem
          sx={{
            '&:hover': {
              backgroundColor: selectedBackground
            },
            borderTop: '1px solid #E8E8ED',
            width: '100%',
            '.MuiMenuItem-root': {
              paddingLeft: '12px',
              paddingRight: '24px'
            }
          }}
          onClick={() => {
            setShowAll(false);
          }}
        >
          <Text sx={{ ...menuItemTextStyles }} variant="subtitle2">
            - View Less
          </Text>
        </MenuItem>
      ) : null}
    </Menu>
  );
};
